import type {
  AiAnalysisSettings,
  AiCategoryMaster,
  AiOutputFieldDef,
  AiOutputFieldValueSource,
  AiOutputFormat,
  DistrictMode,
} from '@citizen-walk/shared-types';

/**
 * 地区の AI 解析設定「プレチェック」(2026-06-06)。
 *
 * 目的: 地区管理者が AI 解析設定を選んだ段階で、設定の不備（出力項目の候補データ未設定・
 * 参照先の語彙データ消失・地区の入力項目との食い違い等）を **設定画面上で先に** 検出し、
 * 「どこを直せばよいか」を一般ユーザに分かる言葉で提示する。これにより、不備があるテンプレを
 * 統合エディタで開いた瞬間に画面が真っ白になる事故 (出力項目の `source` 欠落による
 * `Cannot read properties of undefined (reading 'type')`) を未然に防ぐ。
 *
 * 重要ルール: ここで返す文言は **エンドユーザ (地区管理者) 向け**。`source` / `masterId` /
 * `fieldPath` / `settingsId` といった内部識別子は文言に出さず、表示ラベルや一般語に翻訳する。
 *
 * 本モジュールは pure 関数のみ。実効値の解決 (resolveEffectiveAiAnalysis) や Firestore 取得は
 * 呼び出し側 (DistrictAiAnalysisPanel) が行い、確定値を渡す。
 */

export type PrecheckSeverity = 'error' | 'warning';

export interface PrecheckIssue {
  /** error = このままだと解析が動かない / 画面が開けない。warning = 動くが意図とずれる恐れ。 */
  severity: PrecheckSeverity;
  /** 一般ユーザ向けの問題タイトル (内部 key を含めない)。 */
  title: string;
  /** 修正方法のヒント (一般ユーザ向け)。 */
  hint?: string;
}

export interface PrecheckInput {
  /** この地区で AI 解析が有効か (オフなら何もチェックしない)。 */
  enabled: boolean;
  /** 現在選択されている「使用する AI 解析の設定」の ID (空 = 未選択)。 */
  settingsId: string;
  /** settingsId に対応する設定テンプレ (見つからなければ undefined)。 */
  template: (AiAnalysisSettings & { id: string }) | undefined;
  /** この地区の種類 (種別)。 */
  districtMode: DistrictMode;
  /** 実効的に使われる出力形式 (テンプレ/地区上書き解決後、見つからなければ undefined)。 */
  outputFormat: (AiOutputFormat & { id: string }) | undefined;
  /** 実効的な出力項目一覧 (地区上書きがあればそれ、無ければ出力形式のもの)。 */
  effectiveFields: AiOutputFieldDef[] | undefined;
  /** 利用可能な語彙データ (カテゴリマスタ) 一覧 — 参照先の存在チェック用。 */
  availableMasters: (AiCategoryMaster & { id: string })[];
  /** 実効的に参照する語彙データ ID 一覧。 */
  referencedMasterIds: string[];
  /** この地区で実際に入力できる項目のキー一覧 (地区が独自設定を持つ場合のみ)。 */
  districtInputKeys: string[] | undefined;
  /** AI に送る入力項目の指定 (undefined = 全項目 / 配列 = 限定)。 */
  effectiveInputFieldKeys: string[] | undefined;
}

/** 出力項目を指す一般ユーザ向けの名前 (内部 key は使わず表示ラベル優先、無ければ汎用語)。 */
function fieldDisplayName(f: AiOutputFieldDef): string {
  const label = (f.label ?? '').trim();
  return label || '名称未設定の項目';
}

/**
 * 「候補から選ぶ」項目 (enum-array / code-list-string) のうち、候補の取得元 (source) が
 * 欠落・不完全なものを再帰的に集める。source 欠落は統合エディタのクラッシュ原因なので error。
 */
function collectChoiceSourceProblems(
  fields: AiOutputFieldDef[],
  availableMasterIds: Set<string>,
  out: PrecheckIssue[],
): void {
  for (const f of fields) {
    if (f.kind === 'object-array') {
      collectChoiceSourceProblems(f.itemFields ?? [], availableMasterIds, out);
      continue;
    }
    if (f.kind !== 'enum-array' && f.kind !== 'code-list-string') continue;
    const name = fieldDisplayName(f);
    const source = (f as { source?: AiOutputFieldValueSource }).source;
    if (!source) {
      out.push({
        severity: 'error',
        title: `「${name}」で選べる候補のもとになるデータが設定されていません`,
        hint: 'この項目で選択できる候補（語彙）の取得元を設定してください。設定がないと、このAI解析設定の編集画面を開けません。',
      });
      continue;
    }
    if (source.type === 'master') {
      const id = (source.masterId ?? '').trim();
      if (!id) {
        out.push({
          severity: 'warning',
          title: `「${name}」が参照する語彙データが指定されていません`,
          hint: 'この項目で使う語彙データ（候補の一覧）を選んでください。',
        });
      } else if (!availableMasterIds.has(id)) {
        out.push({
          severity: 'warning',
          title: `「${name}」が参照している語彙データが見つかりません`,
          hint: '参照先の語彙データが削除・変更された可能性があります。設定し直してください。',
        });
      }
    }
  }
}

/**
 * 地区の AI 解析設定をチェックし、修正すべき点を一般ユーザ向けに列挙する。
 * 問題が無ければ空配列を返す。
 */
export function precheckDistrictAiConfig(input: PrecheckInput): PrecheckIssue[] {
  const issues: PrecheckIssue[] = [];

  // AI 解析オフならチェック不要。
  if (!input.enabled) return issues;

  if (!input.settingsId.trim()) {
    issues.push({
      severity: 'error',
      title: 'AI解析の設定が選ばれていません',
      hint: 'この地区で使うAI解析の設定を選んでください。',
    });
    return issues; // これ以上は判定できない
  }
  if (!input.template) {
    issues.push({
      severity: 'error',
      title: '選ばれているAI解析の設定が見つかりません',
      hint: '以前選んだ設定が削除または無効化された可能性があります。設定を選び直してください。',
    });
    return issues; // テンプレが無いと出力形式等を判定できない
  }

  // 出力形式 (結果のかたち)。
  if (!input.outputFormat) {
    issues.push({
      severity: 'error',
      title: '解析結果の出力形式が設定されていません',
      hint: 'AI解析の設定で、結果の出力形式を指定してください。',
    });
  } else if (input.outputFormat.mode !== input.districtMode) {
    issues.push({
      severity: 'warning',
      title: 'この地区の種類に合わない出力形式が選ばれています',
      hint: 'この地区の種類に合った出力形式を選んでください。',
    });
  }

  const availableMasterIds = new Set(input.availableMasters.map((m) => m.id));

  // 出力項目の候補データ (クラッシュ原因の中心)。
  if (input.effectiveFields && input.effectiveFields.length > 0) {
    collectChoiceSourceProblems(input.effectiveFields, availableMasterIds, issues);
  }

  // 参照する語彙データの存在 (同種の警告は1件にまとめる)。
  for (const id of input.referencedMasterIds) {
    if (id && !availableMasterIds.has(id)) {
      issues.push({
        severity: 'warning',
        title: 'AIが参照する語彙データの一部が見つかりません',
        hint: '削除・変更された語彙データを参照している可能性があります。AI解析の設定を確認してください。',
      });
      break;
    }
  }

  // 地区の入力項目との食い違い (地区が独自の入力項目を持つ場合のみ判定)。
  if (
    input.districtInputKeys &&
    input.districtInputKeys.length > 0 &&
    Array.isArray(input.effectiveInputFieldKeys)
  ) {
    const districtKeys = new Set(input.districtInputKeys);
    const missing = input.effectiveInputFieldKeys.filter((k) => k && !districtKeys.has(k));
    if (missing.length > 0) {
      issues.push({
        severity: 'warning',
        title: 'AIに送る入力項目の中に、この地区にない項目が含まれています',
        hint: 'この地区の入力項目に合わせて、AIに送る項目を選び直してください。',
      });
    }
  }

  return issues;
}
